<!-- resources/js/components/VentanillaPanel.vue -->
<template>
    <div class="ventanilla-panel">
        <!-- Header -->
        <div class="header">
            <div class="logo-section">
                <img src="/img/logo_minedu.png" alt="Logo" class="logo-img">
                <div class="logo-text">
                    <h1>SISTEMA GESTION DE COLAS</h1>
                    <p>PANEL DE ATENCION - VENTANILLA</p>
                </div>
            </div>
            <div class="user-info">
                <span class="user-name">👤 {{ user?.nombres || 'Operador' }}</span>
                <button @click="logout" class="logout-btn">Salir</button>
            </div>
        </div>

        <div class="main-content">
            <!-- Loading -->
            <div v-if="loading" class="loading-container">
                <div class="spinner"></div>
                <p>Cargando...</p>
            </div>

            <div v-else>
                <!-- Información de Ventanilla -->
                <div class="ventanilla-info">
                    <div class="info-card">
                        <div class="info-label">Ventanilla</div>
                        <div class="info-value">{{ ventanilla?.codigo }} - {{ ventanilla?.nombre }}</div>
                    </div>
                    <div class="info-card">
                        <div class="info-label">Estado</div>
                        <div class="info-value">
                            <select v-model="ventanilla.estado" @change="cambiarEstado" class="estado-select">
                                <option value="disponible">🟢 Disponible</option>
                                <option value="ocupada">🔴 Ocupada</option>
                                <option value="pausa">🟡 En pausa</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Ticket Actual en Atención -->
                <div v-if="ticketActual" class="ticket-atencion">
                    <div class="atencion-header">
                        <h2>🔔 ATENCIÓN ACTUAL</h2>
                        <div class="tiempo-atencion">
                            ⏱️ Tiempo: {{ tiempoAtencionFormateado }}
                        </div>
                    </div>
                    <div class="ticket-card-grande">
                        <div class="ticket-numero">{{ ticketActual.numero_ticket }}</div>
                        <div class="ticket-info">
                            <div class="info-item">
                                <span class="item-label">Trámite:</span>
                                <span class="item-value">{{ ticketActual.tipo_tramite }}</span>
                            </div>
                            <div class="info-item">
                                <span class="item-label">Preferencia:</span>
                                <span class="item-value">{{ ticketActual.preferencia }}</span>
                            </div>
                            <div class="info-item">
                                <span class="item-label">Inicio:</span>
                                <span class="item-value">{{ fechaFormateada(ticketActual.inicio_atencion) }}</span>
                            </div>
                        </div>
                        <div class="acciones-atencion">
                            <button @click="finalizarAtencion" class="btn-finalizar">
                                ✅ Finalizar Atención
                            </button>
                            <button @click="mostrarModalCancelar" class="btn-cancelar">
                                ❌ Cancelar
                            </button>
                            <button @click="mostrarModalReprogramar" class="btn-reprogramar">
                                📅 Reprogramar
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Botón Llamar Siguiente (si no hay atención) -->
                <div v-else class="llamar-section">
                    <button @click="llamarSiguiente" class="btn-llamar" :disabled="colaPendiente.length === 0">
                        <span class="btn-icon">🎤</span>
                        LLAMAR SIGUIENTE TICKET
                        <span class="btn-count" v-if="colaPendiente.length > 0">{{ colaPendiente.length }}</span>
                    </button>
                </div>

                <!-- Cola de Tickets Pendientes -->
                <div class="cola-pendiente">
                    <div class="cola-header">
                        <h3>📋 TICKETS EN ESPERA</h3>
                        <div class="cola-count">Total: {{ colaPendiente.length }}</div>
                    </div>
                    
                    <div class="tabla-cola">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Ticket</th>
                                    <th>Trámite</th>
                                    <th>Preferencia</th>
                                    <th>Tiempo</th>
                                    <th>Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="ticket in colaPendiente" :key="ticket.id" :class="getPrioridadClass(ticket.prioridad)">
                                    <td>{{ ticket.posicion }}</td>
                                    <td class="ticket-num">{{ ticket.numero_ticket }}</td>
                                    <td>{{ ticket.tipo_tramite }}</td>
                                    <td>
                                        <span class="pref-badge">{{ ticket.preferencia }}</span>
                                    </td>
                                    <td>{{ ticket.tiempo_espera }}</td>
                                    <td>{{ ticket.nombre_usuario }}</td>
                                </tr>
                                <tr v-if="colaPendiente.length === 0">
                                    <td colspan="6" class="sin-datos">No hay tickets en espera</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Estadísticas -->
                <div class="estadisticas">
                    <div class="stat-card">
                        <div class="stat-value">{{ estadisticas.atendidos_hoy }}</div>
                        <div class="stat-label">Atendidos hoy</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">{{ estadisticas.tiempo_promedio }}</div>
                        <div class="stat-label">Tiempo promedio</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">{{ estadisticas.en_espera }}</div>
                        <div class="stat-label">En espera</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Cancelar -->
        <div v-if="modalCancelar" class="modal-overlay" @click.self="modalCancelar = false">
            <div class="modal">
                <div class="modal-header">
                    <h3>Cancelar Atención</h3>
                    <button @click="modalCancelar = false" class="modal-close">✖</button>
                </div>
                <div class="modal-body">
                    <p>Ticket: <strong>{{ ticketActual?.numero_ticket }}</strong></p>
                    <label>Motivo de cancelación:</label>
                    <textarea v-model="motivoCancelacion" class="modal-textarea" rows="3" placeholder="Ingrese el motivo..."></textarea>
                </div>
                <div class="modal-footer">
                    <button @click="confirmarCancelar" class="btn-confirmar">Confirmar Cancelación</button>
                    <button @click="modalCancelar = false" class="btn-cerrar">Cerrar</button>
                </div>
            </div>
        </div>

        <!-- Modal Reprogramar -->
        <div v-if="modalReprogramar" class="modal-overlay" @click.self="modalReprogramar = false">
            <div class="modal">
                <div class="modal-header">
                    <h3>Reprogramar Atención</h3>
                    <button @click="modalReprogramar = false" class="modal-close">✖</button>
                </div>
                <div class="modal-body">
                    <p>Ticket: <strong>{{ ticketActual?.numero_ticket }}</strong></p>
                    <label>Fecha de reprogramación:</label>
                    <input type="datetime-local" v-model="fechaReprogramacion" class="modal-input">
                    <label class="mt-2">Motivo:</label>
                    <textarea v-model="motivoReprogramacion" class="modal-textarea" rows="3" placeholder="Ingrese el motivo..."></textarea>
                </div>
                <div class="modal-footer">
                    <button @click="confirmarReprogramar" class="btn-confirmar">Confirmar Reprogramación</button>
                    <button @click="modalReprogramar = false" class="btn-cerrar">Cerrar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const loading = ref(true)
const ventanilla = ref(null)
const ticketActual = ref(null)
const colaPendiente = ref([])
const estadisticas = ref({
    atendidos_hoy: 0,
    tiempo_promedio: 'N/A',
    en_espera: 0
})
const user = ref(null)
const tiempoAtencion = ref(0)
let timerInterval = null
let inicioAtencionTimestamp = null

// Modales
const modalCancelar = ref(false)
const modalReprogramar = ref(false)
const motivoCancelacion = ref('')
const motivoReprogramacion = ref('')
const fechaReprogramacion = ref('')

const tiempoAtencionFormateado = computed(() => {
    const minutos = Math.floor(tiempoAtencion.value / 60)
    const segundos = tiempoAtencion.value % 60
    return `${minutos.toString().padStart(2, '0')}:${segundos.toString().padStart(2, '0')}`
})

const fechaFormateada = computed(() => {
  return (fechaISO) => {
    if (!fechaISO) return '';
    
    const fecha = new Date(fechaISO);
    
    // Opciones de formato para fecha y hora
    const opciones = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false // Formato de 24 horas
    };

    // Usamos el locale 'es-ES' para el orden correcto DD/MM/AAAA
    return new Intl.DateTimeFormat('es-ES', opciones).format(fecha);
  };
});

// Cargar datos de la ventanilla
const cargarDatos = async () => {
    try {
        const response = await axios.get('/ventanilla/cola-pendiente')
        
        if (response.data.success) {
            const data = response.data.data
            ventanilla.value = data.ventanilla
            colaPendiente.value = data.cola_pendiente
            ticketActual.value = data.ticket_actual
            estadisticas.value = data.estadisticas
            
            // Iniciar timer si hay atención en curso
            if (ticketActual.value) {
                // Verificar si tiene inicio_atencion, si no, usar fecha actual
                const fechaInicio = ticketActual.value.inicio_atencion || new Date().toISOString()
                iniciarTimerAtencion(fechaInicio)
            } else {
                detenerTimerAtencion()
            }
        }
    } catch (error) {
        console.error('Error:', error)
        cargarDatosDemo()
    } finally {
        loading.value = false
    }
}

// Datos de demostración
const cargarDatosDemo = () => {
    user.value = { name: 'Juan Pérez' }
    ventanilla.value = { id: 1, codigo: 'V01', nombre: 'Ventanilla 1', estado: 'disponible' }
    
    colaPendiente.value = [
        { id: 1, numero_ticket: 'ADM-043', tipo_tramite: 'Trámites Administrativos', preferencia: 'Normal', prioridad: 5, tiempo_espera: 'hace 5 min', posicion: 1, nombre_usuario: 'María Gonzales' },
        { id: 2, numero_ticket: 'FIN-044', tipo_tramite: 'Administración Financiera', preferencia: 'Adulto Mayor', prioridad: 1, tiempo_espera: 'hace 8 min', posicion: 2, nombre_usuario: 'Carlos López' },
        { id: 3, numero_ticket: 'RRHH-045', tipo_tramite: 'Recursos Humanos', preferencia: 'Madre gestante', prioridad: 2, tiempo_espera: 'hace 12 min', posicion: 3, nombre_usuario: 'Ana Flores' }
    ]
    
    estadisticas.value = {
        atendidos_hoy: 42,
        tiempo_promedio: '8 min',
        en_espera: 3
    }
    
    loading.value = false
}

// Iniciar timer de atención

const iniciarTimerAtencion = (fechaInicio) => {
    // Detener timer anterior
    detenerTimerAtencion()
    
    // Si no hay fecha de inicio, usar la fecha actual
    if (!fechaInicio && ticketActual.value?.inicio_atencion) {
        fechaInicio = ticketActual.value.inicio_atencion
    }
    
    // Si aún no hay fecha, usar la fecha actual
    if (!fechaInicio) {
        fechaInicio = new Date().toISOString()
    }
    
    // Guardar timestamp de inicio
    inicioAtencionTimestamp = new Date(fechaInicio).getTime()
    
    // Función para actualizar el tiempo
    const actualizarTiempo = () => {
        if (inicioAtencionTimestamp) {
            const ahora = Date.now()
            tiempoAtencion.value = Math.floor((ahora - inicioAtencionTimestamp) / 1000)
        }
    }
    
    // Actualizar inmediatamente
    actualizarTiempo()
    
    // Iniciar intervalo
    timerInterval = setInterval(actualizarTiempo, 1000)
}

const detenerTimerAtencion = () => {
    if (timerInterval) {
        clearInterval(timerInterval)
        timerInterval = null
    }
    inicioAtencionTimestamp = null
    tiempoAtencion.value = 0
}

// Llamar siguiente ticket
const llamarSiguiente = async () => {
    try {
        const response = await axios.post('/ventanilla/llamar-siguiente')
        
        if (response.data.success) {
            // Detener timer actual antes de recargar
            detenerTimerAtencion()
            await cargarDatos()
            // Reproducir sonido
            const audio = new Audio('/sounds/campana.mp3')
            audio.play().catch(e => console.log('Audio no disponible'))
        }
    } catch (error) {
        alert(error.response?.data?.message || 'Error al llamar ticket')
    }
}
// ========== INICIAR ATENCIÓN ==========
const iniciarAtencion = async (idTicket) => {
    try {
        const response = await axios.post(`/ventanilla/iniciar-atencion/${idTicket}`)
        
        if (response.data.success) {
            detenerTimerAtencion()
            await cargarDatos()
        }
    } catch (error) {
        alert(error.response?.data?.message || 'Error al iniciar atención')
    }
}
// Finalizar atención
const finalizarAtencion = async () => {
    if (!ticketActual.value) return
    
    try {
        const response = await axios.post(`/ventanilla/finalizar-atencion/${ticketActual.value.id}`)
        
        if (response.data.success) {
            detenerTimerAtencion()
            await cargarDatos()
            alert('Atención finalizada exitosamente')
        }
    } catch (error) {
        alert(error.response?.data?.message || 'Error al finalizar atención')
    }
}

// Mostrar modal cancelar
const mostrarModalCancelar = () => {
    motivoCancelacion.value = ''
    modalCancelar.value = true
}

// Confirmar cancelar
const confirmarCancelar = async () => {
    if (!ticketActual.value) return
    
    try {
        const response = await axios.post(`/ventanilla/cancelar-atencion/${ticketActual.value.id}`, {
            motivo: motivoCancelacion.value || 'Cancelado por operador'
        })
        
        if (response.data.success) {
            detenerTimerAtencion()
            modalCancelar.value = false
            await cargarDatos()
            alert('Atención cancelada')
        }
    } catch (error) {
        alert(error.response?.data?.message || 'Error al cancelar atención')
    }
}

// Mostrar modal reprogramar
const mostrarModalReprogramar = () => {
    motivoReprogramacion.value = ''
    fechaReprogramacion.value = new Date(Date.now() + 86400000).toISOString().slice(0, 16)
    modalReprogramar.value = true
}

// Confirmar reprogramar
const confirmarReprogramar = async () => {
    if (!ticketActual.value) return
    
    try {
        const response = await axios.post(`/ventanilla/reprogramar-atencion/${ticketActual.value.id}`, {
            fecha_reprogramacion: fechaReprogramacion.value,
            motivo: motivoReprogramacion.value || 'Solicitud del usuario'
        })
        
        if (response.data.success) {
            detenerTimerAtencion()
            modalReprogramar.value = false
            await cargarDatos()
            alert('Atención reprogramada')
        }
    } catch (error) {
        alert(error.response?.data?.message || 'Error al reprogramar atención')
    }
}

// Cambiar estado de ventanilla
const cambiarEstado = async () => {
    try {
        await axios.post('/api/ventanilla/cambiar-estado', {
            estado: ventanilla.value.estado
        })
    } catch (error) {
        console.error('Error al cambiar estado:', error)
    }
}

// Logout
const logout = () => {
    localStorage.removeItem('token')
    router.push('/login')
}

const getPrioridadClass = (prioridad) => {
    if (prioridad === 1) return 'prioridad-alta'
    if (prioridad === 2) return 'prioridad-media'
    return 'prioridad-normal'
}

// Auto-refresh cada 10 segundos
let refreshInterval = null

onMounted(() => {
    cargarDatos()
    refreshInterval = setInterval(() => {
        if (!ticketActual.value) {
            cargarDatos()
        }
    }, 10000)
})

onUnmounted(() => {
    if (timerInterval) clearInterval(timerInterval)
    if (refreshInterval) clearInterval(refreshInterval)
})
</script>

<style scoped>
.ventanilla-panel {
    min-height: 100vh;
    background: #f0f2f5;
}

/* Header */
.header {
    background: #0f1119;
    padding: 15px 30px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: white;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 15px;
}

.logo-img {
    height: 120px;
    width: auto;
    background: #ffffff;
    padding: 5px 20px;
    border-radius: 15px;
    border: 1px solid rgba(255, 140, 0, 0.2);
}

.logo-text h1 {
    margin: 0;
    margin: 0;
    font-size: 28px;
    letter-spacing: 2px;
    color: #ff8c00;
}

.logo-text p {
    margin: 0;
    font-size: 11px;
    opacity: 0.8;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 15px;
}

.logout-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    padding: 8px 20px;
    border-radius: 20px;
    color: white;
    cursor: pointer;
}

.logout-btn:hover {
    background: rgba(255, 255, 255, 0.3);
}

/* Main Content */
.main-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 30px;
}

/* Ventanilla Info */
.ventanilla-info {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    margin-bottom: 25px;
}

.info-card {
    background:linear-gradient(135deg, #0f1119 0%, #1a1d2e 100%);
    border-radius: 12px;
    padding: 15px 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.info-label {
    font-size: 11px;
    color: #f4f6f7;
    margin-bottom: 5px;
}

.info-value {
    font-size: 20px;
    font-weight: bold;
    color: #ff8c00;
}

.estado-select {
    font-size: 16px;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid #ff8c00;
    background: white ;
    cursor: pointer;
}

/* Ticket en Atención */
.ticket-atencion {
    background:linear-gradient(135deg, #0f1119 0%, #1a1d2e 100%);
    border-radius: 16px;
    padding: 25px;
    margin-bottom: 25px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    border-top: 4px solid #2ecc71;
}

.atencion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #ecf0f1;
}

.atencion-header h2 {
    margin: 0;
    font-size: 18px;
    color: #ff8c00;
}

.tiempo-atencion {
    font-family: monospace;
    font-size: 20px;
    font-weight: bold;
    color: #e74c3c;
}

.ticket-card-grande {
    text-align: center;
}

.ticket-numero {
    font-size: 64px;
    font-weight: bold;
    font-family: monospace;
    color: #ff8c00;
    margin-bottom: 20px;
    letter-spacing: 3px;
}

.ticket-info {
    display: flex;
    justify-content: center;
    gap: 30px;
    margin-bottom: 25px;
    flex-wrap: wrap;
}

.info-item {
    background:linear-gradient(135deg, #0f1119 0%, #1a1d2e 100%);
    padding: 8px 15px;
    border: 1px solid #ff8c00;
    border-radius: 8px;
}

.item-label {
    font-size: 11px;
    color: #f5f6f7;
    display: block;
}

.item-value {
    font-size: 14px;
    font-weight: 500;
    color:#ff8c00;
}

.acciones-atencion {
    display: flex;
    justify-content: center;
    gap: 15px;
    flex-wrap: wrap;
}

.btn-finalizar {
    background: #2ecc71;
    color: white;
    padding: 12px 25px;
    border: none;
    border-radius: 10px;
    font-weight: bold;
    cursor: pointer;
}

.btn-finalizar:hover {
    background: #27ae60;
}

.btn-cancelar {
    background: #e74c3c;
    color: white;
    padding: 12px 25px;
    border: none;
    border-radius: 10px;
    font-weight: bold;
    cursor: pointer;
}

.btn-reprogramar {
    background: #f39c12;
    color: white;
    padding: 12px 25px;
    border: none;
    border-radius: 10px;
    font-weight: bold;
    cursor: pointer;
}

/* Llamar Siguiente */
.llamar-section {
    margin-bottom: 25px;
}

.btn-llamar {
    width: 100%;
    background:linear-gradient(135deg, #0f1119 0%, #1a1d2e 100%);
    color: #f39c12;
    padding: 25px;
    border: none;
    border-radius: 16px;
    font-size: 24px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    transition: all 0.3s ease;
}

.btn-llamar:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.btn-llamar:disabled {
    background: #95a5a6;
    cursor: not-allowed;
}

.btn-icon {
    font-size: 32px;
}

.btn-count {
    background: #272626;
    padding: 5px 15px;
    border-radius: 30px;
    font-size: 18px;
}

/* Cola Pendiente */
.cola-pendiente {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    margin-bottom: 25px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.cola-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background: #f8f9fa;
    border-bottom: 1px solid #ecf0f1;
}

.cola-header h3 {
    margin: 0;
    font-size: 16px;
}

.cola-count {
    background: #1e3a5f;
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
}

.tabla-cola {
    overflow-x: auto;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ecf0f1;
}

th {
    background: #f8f9fa;
    font-weight: 600;
    color: #2c3e50;
}

.ticket-num {
    font-family: monospace;
    font-weight: bold;
}

.pref-badge {
    background: #ecf0f1;
    padding: 3px 10px;
    border-radius: 15px;
    font-size: 11px;
}

.prioridad-alta {
    border-left: 3px solid #e74c3c;
}

.prioridad-media {
    border-left: 3px solid #f39c12;
}

.prioridad-normal {
    border-left: 3px solid #2ecc71;
}

.sin-datos {
    text-align: center;
    color: #95a5a6;
    padding: 30px;
}

/* Estadísticas */
.estadisticas {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;
}

.stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-value {
    font-size: 32px;
    font-weight: bold;
    color: #1e3a5f;
}

.stat-label {
    font-size: 11px;
    color: #7f8c8d;
    margin-top: 5px;
}

/* Loading */
.loading-container {
    text-align: center;
    padding: 60px;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #ecf0f1;
    border-top: 4px solid #1e3a5f;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Modales */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal {
    background: white;
    border-radius: 16px;
    width: 450px;
    max-width: 90%;
    overflow: hidden;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background: #1e3a5f;
    color: white;
}

.modal-close {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
}

.modal-body {
    padding: 20px;
}

.modal-body label {
    display: block;
    margin-top: 10px;
    font-weight: 500;
}

.modal-textarea, .modal-input {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 8px;
    margin-top: 5px;
}

.mt-2 {
    margin-top: 10px;
}

.modal-footer {
    padding: 15px 20px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    border-top: 1px solid #ecf0f1;
}

.btn-confirmar {
    background: #2ecc71;
    color: white;
    padding: 8px 20px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
}

.btn-cerrar {
    background: #95a5a6;
    color: white;
    padding: 8px 20px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
}

/* Responsive */
@media (max-width: 768px) {
    .main-content {
        padding: 15px;
    }
    
    .ticket-numero {
        font-size: 40px;
    }
    
    .btn-llamar {
        font-size: 18px;
        padding: 20px;
    }
    
    .estadisticas {
        grid-template-columns: 1fr;
    }
    
    th, td {
        padding: 8px 10px;
        font-size: 12px;
    }
    
    .ticket-info {
        flex-direction: column;
        gap: 10px;
    }
}
</style>